import { useCallback, useMemo, useState } from 'react'

import { LockedFeature } from '../../../components/LockedFeature/LockedFeature'
import { LockedFeatureId, LockedGameSummary } from '../../../types/LockedFeature'
import { useLiveEventsTrackerAccessCheck } from '../../account/hooks/roleHooks'

/**
 * The unlock dialog for links that point at a game outside the user's plan. Mount
 * `lockedGameDialog` once per surface and call `openLockedGameDialog` from every locked link,
 * instead of one dialog per row. Pass the clicked game as the second argument to show it in the dialog.
 *
 * A reader with the Live Events Tracker role is offered the "unlock this game" dialog. One without
 * the role has no Live Events plan to add a game to, so they get the "upgrade your plan" dialog
 * for the feature instead, without the game header. Every caller is a Blueprint Family surface (Live
 * Event Analytics or its Data Glossary tab), so that dialog shows the Live Event Analytics card, the
 * same one /live-event-analytics shows; the role behind both is `live_events_tracker`.
 */
export const useLockedGameDialog = () => {
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()
  const [open, setOpen] = useState(false)
  const [game, setGame] = useState<LockedGameSummary | undefined>(undefined)

  const openLockedGameDialog = useCallback((event?: React.SyntheticEvent, lockedGame?: LockedGameSummary) => {
    event?.preventDefault()
    event?.stopPropagation()
    // Make the clicked trigger the focus owner before the dialog opens. A trigger inside a surface that
    // cancels `mousedown` (e.g. the slider) never takes focus itself; the dialog would then restore focus
    // to an unrelated earlier control on close, and the browser would scroll the page to it.
    const trigger = event?.currentTarget
    if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true })
    setGame(lockedGame)
    setOpen(true)
  }, [])
  const closeLockedGameDialog = useCallback(() => setOpen(false), [])

  const lockedGameDialog = useMemo(
    () =>
      hasLiveEventsTrackerAccess ? (
        <LockedFeature.Dialog lockedFeatureId={LockedFeatureId.LiveEventsGameUnlock} game={game} open={open} onClose={closeLockedGameDialog} />
      ) : (
        <LockedFeature.Dialog lockedFeatureId={LockedFeatureId.LiveEventAnalytics} open={open} onClose={closeLockedGameDialog} />
      ),
    [hasLiveEventsTrackerAccess, open, game, closeLockedGameDialog]
  )

  return { openLockedGameDialog, lockedGameDialog }
}
